import {
    CmsGroup,
    CmsGroupStorageOperations,
    CmsGroupStorageOperationsCreateParams,
    CmsGroupStorageOperationsDeleteParams,
    CmsGroupStorageOperationsGetParams,
    CmsGroupStorageOperationsListParams,
    CmsGroupStorageOperationsUpdateParams
} from "@webiny/api-headless-cms/types";
import { Entity } from "@webiny/db-dynamodb/toolbox";
import WebinyError from "@webiny/error";
import { getClean } from "@webiny/db-dynamodb/utils/get";
import { queryAll, QueryAllParams } from "@webiny/db-dynamodb/utils/query";
import { filterItems } from "@webiny/db-dynamodb/utils/filter";
import { PluginsContainer } from "@webiny/plugins";
import { ValueFilterPlugin } from "@webiny/db-dynamodb/plugins/definitions/ValueFilterPlugin";
import { sortItems } from "@webiny/db-dynamodb/utils/sort";
import { deleteItem, put } from "@webiny/db-dynamodb";

interface PartitionKeyParams {
    tenant: string;
    locale: string;
}
const createPartitionKey = (params: PartitionKeyParams): string => {
    const { tenant, locale } = params;
    return `T#${tenant}#L#${locale}#CMS#CMG`;
};

interface SortKeyParams {
    id: string;
}
const createSortKeys = (params: SortKeyParams): string => {
    const { id } = params;
    return id;
};

interface Keys {
    PK: string;
    SK: string;
}
const createKeys = (params: PartitionKeyParams & SortKeyParams): Keys => {
    return {
        PK: createPartitionKey(params),
        SK: createSortKeys(params)
    };
};

const createType = (): string => {
    return "cms.group";
};

interface CreateGroupsStorageOperationsParams {
    entity: Entity<any>;
    plugins: PluginsContainer;
}
export const createGroupsStorageOperations = (
    params: CreateGroupsStorageOperationsParams
): CmsGroupStorageOperations => {
    const { entity, plugins } = params;

    const filteringPlugins = plugins.byType<ValueFilterPlugin>(ValueFilterPlugin.type);
    if (filteringPlugins.length === 0) {
        throw new WebinyError(
            "DynamoDB filtering plugins not loaded.",
            "MISSING_DYNAMODB_FILTERING_PLUGINS"
        );
    }

    const create = async (params: CmsGroupStorageOperationsCreateParams) => {
        const { group } = params;
        const keys = createKeys(group);
        try {
            await put({
                entity,
                item: {
                    ...group,
                    TYPE: createType(),
                    ...keys
                }
            });
            return group;
        } catch (ex) {
            throw new WebinyError(
                ex.message || "Could not create group.",
                ex.code || "CREATE_GROUP_ERROR",
                {
                    error: ex,
                    group,
                    keys
                }
            );
        }
    };
    const update = async (params: CmsGroupStorageOperationsUpdateParams) => {
        const { group } = params;
        const keys = createKeys(group);
        try {
            await put({
                entity,
                item: {
                    ...group,
                    TYPE: createType(),
                    ...keys
                }
            });
            return group;
        } catch (ex) {
            throw new WebinyError(
                ex.message || "Could not update group.",
                ex.code || "UPDATE_GROUP_ERROR",
                {
                    error: ex,
                    group,
                    keys
                }
            );
        }
    };
    const deleteGroup = async (params: CmsGroupStorageOperationsDeleteParams) => {
        const { group } = params;
        const keys = createKeys(group);
        try {
            await deleteItem({
                entity,
                keys
            });
            return group;
        } catch (ex) {
            throw new WebinyError(
                ex.message || "Could not delete group.",
                ex.code || "DELETE_GROUP_ERROR",
                {
                    error: ex,
                    group,
                    keys
                }
            );
        }
    };
    const get = async (params: CmsGroupStorageOperationsGetParams) => {
        const keys = createKeys(params);

        try {
            return await getClean<CmsGroup>({
                entity,
                keys
            });
        } catch (ex) {
            throw new WebinyError(
                ex.message || "Could not get group.",
                ex.code || "GET_GROUP_ERROR",
                {
                    error: ex,
                    ...params,
                    keys
                }
            );
        }
    };
    const list = async (params: CmsGroupStorageOperationsListParams) => {
        const { sort, where } = params;

        const queryAllParams: QueryAllParams = {
            entity,
            partitionKey: createPartitionKey(where),
            options: {
                gte: " "
            }
        };

        let records: CmsGroup[] = [];
        try {
            records = await queryAll(queryAllParams);
        } catch (ex) {
            throw new WebinyError(
                ex.message || "Could not list groups.",
                ex.code || "LIST_GROUP_ERROR",
                {
                    error: ex,
                    ...params,
                    sort,
                    where
                }
            );
        }

        const filteredItems = filterItems({
            items: records,
            where,
            fields: [],
            plugins
        });
        if (!sort || sort.length === 0) {
            return filteredItems;
        }
        return sortItems({
            items: filteredItems,
            sort,
            fields: []
        });
    };

    return {
        create,
        update,
        delete: deleteGroup,
        get,
        list
    };
};
